import clsx from 'clsx';
import React from 'react';
import { When } from 'react-if';

import cn from '@/lib/clsxm';
import { priceFormatter } from '@/lib/helper';

import { SIZE_ENUM } from '@/components/@base/@helpers/types';
import Container from '@/components/@base/container';
import Group from '@/components/@base/input/@shared/group';
import Label from '@/components/@base/input/@shared/label';
import CheckBox from '@/components/@base/input/check-box';
import Loading from '@/components/@base/loading';
import Tooltip from '@/components/@base/tooltip';
import { Text } from '@/components/@base/typography';
import XImage from '@/components/@base/x-image';
import Props from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/options-item/type';
import { ADDONS_ENUM } from '@/components/shared/components/add-to-cart-button/add-to-cart-modal/type';

import {
  DATA_CY_ITEM_PRICE,
  DATA_CY_LAST_PRICE_CONTAINER,
  DATA_CY_OPTIONS,
  DATA_CY_OPTIONS_ITEM,
  DATA_CY_OPTIONS_ITEM_LABEL,
} from '@/constant/cypress';
import imagesList from '@/constant/images-list';
import localization from '@/constant/localization';

export default function OptionsItem({
  label,
  price,
  additionalItems,
  hasCheckbox = false,
  onChange,
  className,
  disabled = false,
  checked = false,
  noSign = false,
  showCurrency = true,
  finalLastPrice = 0,
  discount = 0,
  lastPrice = 0,
  saleEndedAt,
  type,
  subTitle,
  tooltipClassName,
  typeItem,
  showFakeCheckbox,
  showFakeChecked,
  isLoading,
  labelClassName,
  finalLastPriceClassName,
  lastPriceClassName,
  priceClassName,
  currencyClassName,
  showCircle,
  groupClassName,
  checkBoxClassName,
}: Props) {
  const sign = price > 0 ? '+' : price < 0 ? '-' : '';
  const currentTimeInSeconds = Math.floor(Date.now() / 1000);
  const discountIsValid = saleEndedAt ? saleEndedAt > currentTimeInSeconds : true;
  return (
    <Container
      center
      className={cn('justify-between py-4', className)}
      data-cy={DATA_CY_OPTIONS}
    >
      <Group className={cn('relative flex items-center gap-0.5', groupClassName)}>
        {hasCheckbox &&
          (isLoading ? (
            <div className='h-[18px] w-[18px]'>
              <Loading />
            </div>
          ) : (
            <CheckBox
              data-cy={DATA_CY_OPTIONS_ITEM}
              onChange={(checked) => onChange && onChange(checked)}
              disabled={disabled}
              checked={checked}
              showFakeChecked={showFakeChecked}
              className={cn(
                'z-40! ml-1.5 bg-[#E5E8EB]',
                showFakeCheckbox &&
                  'pointer-events-none before:absolute before:top-[1px] before:right-0 before:h-[18px] before:w-[18px] before:rounded-sm before:bg-[#E5E8EB]',
                disabled && 'border-[#F4F5F6] bg-[#F4F5F6]',
                checkBoxClassName,
              )}
              showCircle={showCircle}
            />
          ))}

        <Label
          data-cy={DATA_CY_OPTIONS_ITEM_LABEL}
          className={clsx(
            labelClassName,
            'line-clamp-1 flex-1 text-sm text-[#5B5C60]',
            disabled && 'cursor-default text-[#878F9B]',
          )}
        >
          {label}
        </Label>
        {additionalItems}
        <When condition={subTitle}>
          <Container>
            <XImage
              className='shrink-0'
              src={imagesList.icons.infoIcon}
              alt={subTitle || ''}
              width={16}
              height={16}
              data-tooltip-id='dynamicAddon'
              data-tooltip-content={subTitle}
            />
            <Tooltip
              id='dynamicAddon'
              place='top'
              positionStrategy='fixed'
              className={tooltipClassName}
            />
          </Container>
        </When>
      </Group>
      <Container center className={clsx(priceClassName, 'gap-[7px]')}>
        {/*{finalLastPrice > 0 && (*/}
        {/*  <Text className='-mb-1 text-[#BCA087] line-through'>*/}
        {/*    {priceFormatter(Math.abs(price))}*/}
        {/*  </Text>*/}
        {/*)}*/}
        {discount > 0 && lastPrice > 0 && discountIsValid && (
          <Container
            className={clsx(
              'mb-1.5 flex h-[26.5px] w-[26px] items-center justify-center rounded-[5px] border-[1px] border-[#FFFFFF] bg-[linear-gradient(257.48deg,_#FF9606_4.12%,_#F9BB00_95.45%)] shadow-[0px_4px_12px_-5px_#FF7B016E]',
              disabled &&
                'bg-[linear-gradient(257.48deg,_#FFF.12%,_#C1C3C8.45%)] shadow-[0px_4px_12px_-5px_#878F9B]',
            )}
          >
            <Text bold className='text-xs text-[#FFFFFF]'>
              {discount}&#37;
            </Text>
          </Container>
        )}
        <Container>
          {lastPrice > 0 && discountIsValid && (
            <Text
              className={cn(
                lastPriceClassName,
                '-mb-1.5 text-left text-sm font-bold text-[#BFBFBF] line-through',
                showCurrency && price !== 0 && 'pl-8',
                typeItem === ADDONS_ENUM.EXTRA_LICENSE && (showCurrency ? 'pl-10' : 'pl-2.5'),
                disabled && 'text-[#C1C3C8]',
              )}
            >
              {priceFormatter(Math.abs(lastPrice))}
              {!noSign && typeItem !== ADDONS_ENUM.EXTRA_LICENSE && sign}
            </Text>
          )}
          <Container
            className='flex items-center gap-1'
            data-cy={DATA_CY_LAST_PRICE_CONTAINER}
          >
            <Text
              data-cy={DATA_CY_ITEM_PRICE}
              size={SIZE_ENUM.LG}
              bold
              className={cn(
                finalLastPriceClassName,
                'w-full text-left text-[#424244]',
                disabled && 'text-[#878F9B]',
              )}
            >
              {finalLastPrice > 0
                ? priceFormatter(Math.abs(finalLastPrice))
                : priceFormatter(Math.abs(price))}
              {!noSign && sign}
            </Text>
            {showCurrency && price !== 0 && (
              <Text
                className={clsx(
                  'text-[13px] text-[#76767C]',
                  currencyClassName,
                  disabled && 'text-[#878F9B]',
                )}
              >
                {localization.IRCurrency}
              </Text>
            )}
          </Container>
        </Container>
      </Container>
    </Container>
  );
}
